import * as api from '@gitbook/api-client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { HiveBranchProvider, HiveRevisionProvider, useHiveClient } from '@gitbook/hive-react';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assertIsDefined } from '@gitbook/util-ts';

/**
 * Provide a Hive context to a React tree for the space.
 */
export function WithSpaceHiveContext(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    readOnly: boolean;
    screen: NavigationScreenSpaceState;
    notFound?: React.ReactNode;
    children: React.ReactNode;
}): React.ReactElement {
    const { space, changeRequest, readOnly, screen, notFound = null, children } = props;

    const hiveClient = useHiveClient();

    const isFrozenContent =
        screen.revision !== undefined ||
        (screen.changeRequest &&
            changeRequest &&
            [api.ChangeRequestStatus.Merged, api.ChangeRequestStatus.Archived].includes(
                changeRequest.status
            ));

    if (isFrozenContent) {
        const revisionId = screen.revision || changeRequest?.revision;
        assertIsDefined(revisionId);

        const revision = getSuspense(
            hiveClient.store.getRevision(screen.space, revisionId, { metadata: false })
        );

        if (!revision) {
            return <>{notFound}</>;
        }

        return <HiveRevisionProvider revision={revision}>{children}</HiveRevisionProvider>;
    }

    return (
        <HiveBranchProvider
            contentId={space.id}
            branchId={changeRequest ? getChangeRequestBranchId(changeRequest.id) : undefined}
            readOnly={readOnly}
        >
            {children}
        </HiveBranchProvider>
    );
}
